import React, { useEffect, useState, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getDirectors, createDirector, updateDirector, deleteDirector } from './api';
import DirectorVideoOrderPanel from './DirectorVideoOrderPanel'; // Import the panel

export default function DirectorsPanel() {
  const { token } = useOutletContext();
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', bio: '', image: '', active: true });
  const [editingId, setEditingId] = useState(null);

  const fetchDirectors = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getDirectors(token);
      setDirectors(data);
    } catch (err) {
      setError('Failed to load directors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    if (token) {
      fetchDirectors(); 
    }
  }, [token]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      // Create a copy of the form data with the correct field names
      const directorData = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        bio: form.bio,
        image: form.image,
        active: form.active
      };

      if (editingId) {
        await updateDirector(editingId, directorData, token);
      } else {
        await createDirector(directorData, token);
      }
      
      // Reset form
      setForm({ firstName: '', lastName: '', bio: '', image: '', active: true });
      setEditingId(null);
      fetchDirectors();
    } catch (err) {
      console.error('Error saving director:', err);
      setError('Failed to save director: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEdit = d => {
    setEditingId(d.id);
    // Split the name into first and last names
    const names = d.name ? d.name.split(' ') : ['', ''];
    setForm({ 
      firstName: d.first_name || names[0] || '', 
      lastName: d.last_name || names.slice(1).join(' ') || '', 
      bio: d.bio || '', 
      image: d.image || '', 
      active: !!d.active 
    });
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this director?')) return;
    try {
      await deleteDirector(id, token);
      fetchDirectors();
    } catch (err) {
      setError('Failed to delete director');
    }
  };

  return (
    <div style={{ margin: '32px 0' }}>
      <h3>Directors</h3>
      {loading ? <div>Loading...</div> : null}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      <form onSubmit={handleSubmit} style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <input 
            name="firstName" 
            value={form.firstName} 
            onChange={handleChange} 
            placeholder="First Name" 
            required 
            style={{ marginRight: 8 }} 
          />
          <input 
            name="lastName" 
            value={form.lastName} 
            onChange={handleChange} 
            placeholder="Last Name" 
            required 
            style={{ marginRight: 8 }} 
          />
        </div>
        <input type="file" accept="image/jpeg,image/jpg" onChange={e => {
  const file = e.target.files[0];
  if (file && file.type.match(/^image\/(jpeg|jpg)$/)) {
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, image: ev.target.result }));
    reader.readAsDataURL(file);
  } else {
    setForm(f => ({ ...f, image: '' }));
  }
}} style={{ marginRight: 8 }} />
{form.image && <img src={form.image} alt="preview" style={{ width: 40, verticalAlign: 'middle', marginLeft: 8 }} />}
        <label style={{ marginRight: 8 }}>
          <input type="checkbox" name="active" checked={form.active} onChange={handleChange} /> Active
        </label>
        <br />
        <textarea name="bio" value={form.bio} onChange={handleChange} placeholder="Bio" rows={6} style={{ width: 400, marginTop: 8 }} />
        <br />
        <button type="submit" style={{ marginTop: 8 }}>{editingId ? 'Update' : 'Add'} Director</button>
        {editingId && <button type="button" onClick={() => { setEditingId(null); setForm({ firstName: '', lastName: '', bio: '', image: '', active: true }); }} style={{ marginLeft: 8 }}>Cancel</button>}
      </form>

      {/* Conditionally render DirectorVideoOrderPanel when editing a director */}
      {editingId && (
        <div style={{ marginTop: '30px', borderTop: '1px solid #ccc', paddingTop: '20px' }}>
          <h4>Manage Video Order for {form.firstName} {form.lastName}</h4>
          <DirectorVideoOrderPanel directorId={editingId} />
        </div>
      )}
      <table border="1" cellPadding="6" style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Active</th>
            <th>Image</th>
            <th>Bio</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {directors.map(d => (
            <tr key={d.id}>
              <td>{d.first_name || 'N/A'}</td>
              <td>{d.last_name || 'N/A'}</td>
              <td>{d.active ? 'Yes' : 'No'}</td>
              <td>{d.image ? <img src={d.image} alt="" style={{ width: 40 }} /> : 'None'}</td>
              <td>{d.bio || 'No bio'}</td>
              <td>
                <button onClick={() => handleEdit(d)}>Edit</button>
                <button onClick={() => handleDelete(d.id)} style={{ marginLeft: 8, color: 'red' }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
